import React, { ReactNode } from "react";
import { Box, styled, Theme, Typography } from "@mui/material";
import { TFunction } from "react-i18next";
import StarIcon from "@icons/myicons/star-3.svg?react";
import InvestmentIcon from "@icons/myicons/leaf-1.svg?react";
import CheckIcon from "@icons/check1.svg?react";
import CrossIcon from "@icons/cross.svg?react";
import RetainedIcon from "@icons/myicons/retained.svg?react";
import DistributingIcon from "@icons/myicons/refresh.svg?react";
import { Courtage } from "@rentablo/api/apiv1";

export const NO_VALUE = "-";

export const IconBoxWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(5)};
  ${({ theme }) => theme.breakpoints.down("sm")} {
    gap: ${({ theme }) => theme.spacing(2)};
  }
`;

export const IconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bgColor" && prop !== "iconBorderColor",
})<{
  bgColor: string;
  iconBorderColor: string;
}>`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid ${({ iconBorderColor }) => iconBorderColor};
  color: ${({ iconBorderColor }) => iconBorderColor};
  background-color: ${({ bgColor }) => bgColor};
`;

export const createIconRow = (
  theme: Theme,
  icon: "star" | "leaf",
  count?: number
) => {
  if (count === undefined) return NO_VALUE;
  return (
    <Box display="flex" alignItems="center" gap={2.5}>
      {Array.from({ length: count }, (_, index) => (
        <Box key={index}>
          {icon === "star" ? (
            <StarIcon color={theme.palette.primary.main} />
          ) : (
            <InvestmentIcon color={theme.palette.icon.success} />
          )}
        </Box>
      ))}
    </Box>
  );
};

export const createBankRow = (commisions?: Courtage[]) => {
  if (!commisions || commisions.length === 0) return NO_VALUE;
  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      {commisions.map((commission) => (
        <Box display="flex" alignItems="center" gap={2.5}>
          <Typography variant="body2">{commission.bankName}</Typography>
        </Box>
      ))}
    </Box>
  );
};

export const createBooleanRow = (theme: Theme, value?: boolean) => {
  if (value === undefined) return NO_VALUE;
  return value ? (
    <IconBox
      bgColor={theme.palette.background.success}
      iconBorderColor={theme.palette.icon.success}
    >
      <CheckIcon />
    </IconBox>
  ) : (
    <IconBox
      bgColor={theme.palette.background.error}
      iconBorderColor={theme.palette.icon.error}
    >
      <CrossIcon style={{ width: 16, height: 16 }} />
    </IconBox>
  );
};

export const createIconTextRow = (
  theme: Theme,
  text?: ReactNode,
  icon?: ReactNode
) => {
  if (!text || !icon) return NO_VALUE;
  return (
    <IconBoxWrapper>
      <IconBox
        bgColor={theme.palette.background.selected}
        iconBorderColor={theme.palette.icon.active}
      >
        {icon}
      </IconBox>
      <Typography variant="body2" fontWeight={600}>
        {text}
      </Typography>
    </IconBoxWrapper>
  );
};

export const createDistributionTypeRow = (
  theme: Theme,
  t: TFunction,
  value?: string
) => {
  if (!value) return NO_VALUE;
  return createIconTextRow(
    theme,
    t(`productBasicInfo.distribution.${value}`),
    distributionTypeIcons[value as keyof typeof distributionTypeIcons]
  );
};

export const distributionTypeIcons = {
  RETAINED: <RetainedIcon />,
  DISTRIBUTING: <DistributingIcon />,
};
